import { ChevronDown } from "lucide-react"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { UserMenuContent } from "@/components/user-menu-content"
import { useCurrentContext } from "@/hooks/use-current-context"
import { useInitials } from "@/hooks/use-initials"

/**
 * HeaderNavUser — the header-right user dropdown for brand/agency + admin shells.
 *
 * Spec (CTPO-locked): NavUser is relocated from sidebar-footer to the header
 * right slot. Composition mirrors the existing `<NavUser>` but sized for a
 * compact header avatar trigger.
 *
 * Reads `currentUser` from the OKS-5 shared-props contract via
 * `useCurrentContext()`. Renders nothing when `currentUser` is `null`
 * (defensive — every shell is mounted under an authed route).
 */
export function HeaderNavUser() {
  const { currentUser } = useCurrentContext()
  const getInitials = useInitials()

  if (!currentUser) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant="ghost"
            size="sm"
            className="flex h-9 items-center gap-2 rounded-full px-1.5"
            data-testid="header-nav-user-trigger"
            aria-label={currentUser.name}
          >
            <Avatar className="size-7 overflow-hidden rounded-full">
              <AvatarImage
                src={currentUser.avatar_url ?? undefined}
                alt={currentUser.name}
              />
              <AvatarFallback className="rounded-lg bg-neutral-200 text-xs text-black dark:bg-neutral-700 dark:text-white">
                {getInitials(currentUser.name)}
              </AvatarFallback>
            </Avatar>
            <ChevronDown className="size-4 opacity-60" aria-hidden="true" />
          </Button>
        }
      />
      <DropdownMenuContent className="w-56" align="end">
        <UserMenuContent />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
